import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo, SITE_URL } from "@shared/components";
import { BLOG_POSTS, type BlogPostConfig } from "@shared/config/blog-posts";
import { ArrowRight } from "lucide-react";

const META_TITLE = "Блог Polotno — статьи о транскрибации аудио и видео в текст";
const META_DESCRIPTION = "Разбор цен, форматов записи и практических сценариев расшифровки аудио и видео в текст.";

const ALL_FILTER = "Все";

const formatDate = (date: string): string => new Date(`${date}T00:00:00`).toLocaleDateString("ru-RU");

/** Точка-разделитель между рубрикой и временем чтения */
const MetaDot: React.FC = () => <span className="h-[3px] w-[3px] shrink-0 rounded-full bg-muted-foreground/40" />;

/** Крупная карточка: занимает две колонки и открывает ленту */
const FeaturedCard: React.FC<{ post: BlogPostConfig }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex min-h-[264px] flex-col justify-between gap-5 rounded-lg border border-border bg-muted/50 p-5 transition-colors hover:border-primary md:col-span-2 md:gap-8 md:p-8"
  >
    <div className="flex flex-col gap-2.5 md:gap-3">
      <div className="flex items-center gap-2 md:gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground md:text-xs">
          {post.category}
        </span>
        <MetaDot />
        <span className="text-[11px] text-muted-foreground/70 md:text-xs">{post.readingMinutes} мин чтения</span>
      </div>
      <h2 className="max-w-[560px] text-2xl font-semibold leading-[1.2] tracking-[-0.02em] text-foreground md:text-[32px]">
        {post.title}
      </h2>
      <p className="max-w-[560px] text-[15px] leading-relaxed text-muted-foreground">{post.excerpt}</p>
    </div>
    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
      Читать статью
      <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
    </div>
  </Link>
);

/** Обычная карточка ленты */
const PostCard: React.FC<{ post: BlogPostConfig }> = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="flex flex-col justify-between gap-3 rounded-lg border border-border p-5 transition-colors hover:border-primary md:min-h-[236px] md:gap-6 md:p-6"
  >
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-2 md:gap-2.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground md:text-xs">
          {post.category}
        </span>
        <MetaDot />
        <span className="text-[11px] text-muted-foreground/70 md:text-xs">{post.readingMinutes} мин</span>
      </div>
      <h2 className="text-[19px] font-semibold leading-[1.3] tracking-[-0.015em] text-foreground md:text-xl">
        {post.title}
      </h2>
      <p className="text-sm leading-relaxed text-muted-foreground">{post.excerpt}</p>
    </div>
    <span className="text-[13px] text-muted-foreground/70">{formatDate(post.datePublished)}</span>
  </Link>
);

export const BlogIndexPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>(ALL_FILTER);

  const categories = useMemo(() => [ALL_FILTER, ...new Set(BLOG_POSTS.map(post => post.category))], []);

  const visiblePosts = useMemo(
    () => (activeCategory === ALL_FILTER ? BLOG_POSTS : BLOG_POSTS.filter(post => post.category === activeCategory)),
    [activeCategory]
  );

  const [featuredPost, ...restPosts] = visiblePosts;

  const collectionSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: META_TITLE,
    description: META_DESCRIPTION,
    url: `${SITE_URL}/blog`,
    inLanguage: "ru-RU",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: BLOG_POSTS.length,
      itemListElement: BLOG_POSTS.map((post, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: post.metaTitle,
        url: `${SITE_URL}/blog/${post.slug}`
      }))
    }
  };

  return (
    <div className="overflow-hidden">
      <Seo title={META_TITLE} description={META_DESCRIPTION} path="/blog" jsonLd={[collectionSchema]} />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "Блог" }]} />

      <section className="border-b border-border pb-6 pt-8 md:pb-12 md:pt-16">
        <div className="container">
          <div className="flex flex-col gap-3 md:items-center md:gap-5 md:text-center">
            <h1 className="text-[34px] font-bold leading-[1.1] tracking-[-0.03em] text-foreground md:text-5xl">Блог</h1>
            <p className="max-w-[620px] text-base leading-relaxed text-muted-foreground md:text-lg">
              {META_DESCRIPTION}
            </p>

            {/* На мобильном чипы уезжают в горизонтальную прокрутку, чтобы не ломать сетку */}
            <div className="-mx-4 mt-1 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:justify-center md:px-0">
              {categories.map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`flex h-9 shrink-0 items-center rounded-full px-3.5 text-sm transition-colors md:h-8 md:text-[13px] ${
                    activeCategory === category
                      ? "bg-primary font-medium text-primary-foreground"
                      : "border border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-6 pt-6 md:pb-6 md:pt-12">
        <div className="container">
          <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {featuredPost && <FeaturedCard post={featuredPost} />}
            {restPosts.map(post => (
              <PostCard key={post.slug} post={post} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-10 pt-6 md:pb-20 md:pt-10">
        <div className="container">
          <div className="flex flex-col gap-4 rounded-lg border border-primary p-5 md:flex-row md:items-center md:justify-between md:gap-12 md:p-8">
            <div className="flex flex-col gap-1.5">
              <p className="text-[19px] font-semibold tracking-[-0.02em] text-foreground md:text-xl">
                Проверьте на своей записи
              </p>
              <p className="text-sm text-muted-foreground md:text-[15px]">Пробный период — 150 минут за 99 ₽.</p>
            </div>
            <Link
              to="/"
              className="flex h-12 shrink-0 items-center justify-center rounded-md bg-primary px-5 text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 md:h-11"
            >
              Загрузить файл
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
